import { Drawer as DrawerBase } from '@base-ui/react/drawer'
import map from 'lodash/map'
import toLower from 'lodash/toLower'
import React from 'react'

import { ampli } from '@Analytics/ampli'
import { Drawer } from '@Components/Drawer'
import { DrawerContent } from '@Components/Drawer/Content'
import { Alignment } from '@Components/Drawer/types'
import { Button } from '@CoreComponents/Buttons/Button'
import { DrawerDataFragment, PageIdentifier, PagePresentation, PageType, useDrawerPageLazyQuery, useDrawerPagesLazyQuery } from '@Generated/graphql'

import styles from './Drawer.module.scss'

const parseSettings = (settings?: DrawerDataFragment['settings']) => {
  const result: {
    alignment: Alignment
  } = {
    alignment: 'right',
    ...(typeof settings === 'object' && !Array.isArray(settings) ? settings || {} : {}),
  }

  return result
}

export const DrawerPageSync = ({
  children,
  footer,
  onOpenChange,
  open: openProp,
  page,
  presentation = PagePresentation.Modal,
  showError = false,
  sourceID,
  ...props
}: React.ComponentProps<typeof Drawer> & {
  footer?: React.ReactNode
  page?: DrawerDataFragment
  presentation?: PagePresentation
  showError?: boolean
  sourceID?: string
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const handleOpenChange = React.useCallback(
    async (open: boolean, eventDetails: DrawerBase.Root.ChangeEventDetails) => {
      try {
        if (!open) {
          return
        }

        ampli.modalOpen({
          dest_page_type_id: page?.id,
          dest_page_type: toLower(page?.type),
          source_id: sourceID,
        })
      } finally {
        setDialogOpen(open)
        onOpenChange?.(open, eventDetails)
      }
    },
    [page?.id, page?.type, onOpenChange, sourceID],
  )

  const settings = parseSettings(page?.settings)

  return (
    <Drawer
      {...props}
      alignment={settings.alignment}
      loading={false}
      onOpenChange={handleOpenChange}
      open={openProp ?? dialogOpen}
    >
      <DrawerContent
        blocks={page?.blocks || []}
        footer={footer}
        pageID={page?.id || undefined}
        pageType={page?.type || undefined}
        presentation={presentation}
        showError={showError}
        tabs={page?.modalTabs || []}
        title={page?.title}
      >
        {children}
      </DrawerContent>
    </Drawer>
  )
}

export const DrawerPageAsync = ({
  children,
  footer,
  onOpenChange,
  onOpenChangeComplete,
  open: openProp,
  pageID,
  pageType,
  presentation = PagePresentation.Modal,
  sourceID,
  triggerRender,
}: {
  children?: React.ReactNode
  footer?: React.ReactNode
  onOpenChange?: React.ComponentProps<typeof Drawer>['onOpenChange']
  onOpenChangeComplete?: React.ComponentProps<typeof Drawer>['onOpenChangeComplete']
  open?: boolean
  pageID: string
  pageType: PageType
  presentation?: PagePresentation
  sourceID?: string
  triggerRender?: React.ComponentProps<typeof Drawer>['triggerRender']
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const [runQuery, { data, error, loading }] = useDrawerPageLazyQuery()
  const showError = !!error || (!!data && !data.page)

  const handleOpenChange = React.useCallback(
    async (open: boolean, eventDetails: DrawerBase.Root.ChangeEventDetails) => {
      try {
        if (!open) {
          return
        }

        // TODO: consider having a failure state, if runQuery fails?
        ampli.modalOpen({
          dest_page_type_id: pageID,
          dest_page_type: toLower(pageType),
          source_id: sourceID,
        })

        await runQuery({
          variables: {
            id: pageID,
            type: pageType,
            presentation,
          },
        })
      } finally {
        setDialogOpen(open)
        onOpenChange?.(open, eventDetails)
      }
    },
    [runQuery, pageID, onOpenChange, pageType, presentation, sourceID],
  )

  const settings = parseSettings(data?.page?.settings)

  return (
    <Drawer
      alignment={settings.alignment}
      loading={loading}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      open={openProp ?? dialogOpen}
      triggerRender={triggerRender}
    >
      <DrawerContent
        blocks={data?.page?.blocks || []}
        footer={footer}
        pageID={pageID}
        pageType={pageType}
        presentation={presentation}
        showError={showError}
        tabs={data?.page?.modalTabs || []}
        title={data?.page?.title}

      >
        {children}
      </DrawerContent>
    </Drawer>
  )
}

export const DrawerPagesAsync = ({
  onOpenChange,
  onOpenChangeComplete,
  open: openProp,
  pages,
  presentation = PagePresentation.Modal,
  settings = {},
  sourceID,
  title,
  triggerRender,
}: {
  onOpenChange?: React.ComponentProps<typeof Drawer>['onOpenChange']
  onOpenChangeComplete?: React.ComponentProps<typeof Drawer>['onOpenChangeComplete']
  open?: boolean
  pages: PageIdentifier[]
  presentation?: PagePresentation
  settings?: DrawerDataFragment['settings']
  sourceID?: string
  title?: React.ReactNode
  triggerRender?: React.ComponentProps<typeof Drawer>['triggerRender']
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const [runQuery, { data, error, loading }] = useDrawerPagesLazyQuery()
  const showError = !!error || (!!data && (!data.pages || data.pages.length === 0))

  const handleOpenChange = React.useCallback(
    async (open: boolean, eventDetails: DrawerBase.Root.ChangeEventDetails) => {
      try {
        if (!open) {
          return
        }

        ampli.modalOpen({
          dest_page_type_id: '1',
          dest_page_type: 'drawer_page_selector',
          source_id: sourceID,
        })

        await runQuery({
          variables: {
            pages,
            presentation,
          },
        })
      } finally {
        setDialogOpen(open)
        onOpenChange?.(open, eventDetails)
      }
    },
    [runQuery, onOpenChange, pages, presentation, sourceID],
  )

  const parsedSettings = parseSettings(settings)

  if (pages && pages.length === 1) {
    return (
      <DrawerPageAsync
        onOpenChange={onOpenChange}
        onOpenChangeComplete={onOpenChangeComplete}
        pageID={pages[0].id}
        pageType={pages[0].type}
        presentation={presentation}
        sourceID={sourceID}
        triggerRender={triggerRender}
      />
    )
  }

  return (
    <Drawer
      alignment={parsedSettings.alignment}
      loading={loading}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      open={openProp ?? dialogOpen}
      triggerRender={triggerRender}
    >
      <DrawerContent
        presentation={presentation}
        showError={showError}
        title={title}
      >
        <div className={styles.pagesAsyncWrapper}>
          {map(data?.pages, page => (
            <DrawerPageSync
              key={page.id}
              page={page}
              presentation={presentation}
              showError={false}
              sourceID={sourceID}
              triggerRender={props => (
                <Button {...props} className="" innerSpacing="medium" theme="white" type="button">
                  {page.title}
                </Button>
              )}
            />
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
